import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { useSsrBootstrap, SsrRequestContext } from "@/lib/ssr-bootstrap";
import { useUser } from "@/hooks/useUser";
import { getSupabaseClient } from "@/lib/supabase/client";
import {
  FEATURE_FLAG_DEFAULTS,
  HOST_CONFIRMATION_CALLS_FLAG,
  isFeatureFlagKey,
  resolveFlag,
  type FeatureFlagKey,
} from "@workspace/feature-flags-shared";

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;
// Release 8.1: never enable new browser evaluation logging via old env values.
const EVALUATION_LOGGING_ENABLED = false;

// Re-export the shared key union so call sites (`useFeatureFlag("...")`)
// get autocomplete + a compile-time error when a key is renamed in the
// shared package. The mobile app imports the same union from
// `@workspace/feature-flags-shared`, so a typo on one surface can't
// silently diverge from the other.
export type { FeatureFlagKey } from "@workspace/feature-flags-shared";

interface FeatureFlagContextType {
  flags: Record<string, boolean>;
  loading: boolean;
  isEnabled: (flagName: string) => boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextType>({
  flags: {},
  loading: true,
  isEnabled: () => false,
});

// Task #6110 — the flags fetch MUST settle within a bounded window. The
// checkout Pay button is disabled while `loading` is true (Task #5038's
// pfcFlagLoading gate), so a fetch that hangs indefinitely (stalled proxy,
// dead connection that never errors) left the Pay button silently disabled
// forever with no message — an unexplained checkout "hang". On timeout we
// resolve to {} and the shared FEATURE_FLAG_DEFAULTS take over, exactly the
// same degraded-but-functional state as a non-2xx response.
export const FLAGS_FETCH_TIMEOUT_MS = 10_000;

async function fetchFlags(token?: string): Promise<Record<string, boolean>> {
  const headers: HeadersInit = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
    headers["Content-Type"] = "application/json";
  }

  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => {
        try { controller.abort(); } catch { /* noop */ }
      }, FLAGS_FETCH_TIMEOUT_MS)
    : null;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/config/feature-flags`, {
      headers,
      ...(controller ? { signal: controller.signal } : {}),
    });
  } finally {
    if (timer) clearTimeout(timer);
  }
  if (!res.ok) return {};
  try {
    const data = await res.json();
    return data.flags || {};
  } catch {
    // A 2xx response with an empty or non-JSON body (e.g. an HTML error
    // page served by an edge/proxy, or a truncated body) makes res.json()
    // throw — Safari surfaces it as a DOMException ("The string did not
    // match the expected pattern."). Treat it as "no flags" rather than
    // letting it bubble up as an unhandled rejection.
    return {};
  }
}

async function logFlagEvaluation(flagKey: string, userId: string | null, variant: boolean, context?: string) {
  if (!EVALUATION_LOGGING_ENABLED || typeof navigator === "undefined" || navigator.webdriver ||
      /bot|crawler|spider|headless|prerender|playwright|puppeteer/i.test(navigator.userAgent)) return;
  try {
    if (sessionStorage.getItem("bac.flag-evaluation-sent")) return;
    sessionStorage.setItem("bac.flag-evaluation-sent", "1");
    await fetch(`${API_BASE}/config/feature-flags/log`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        flag_key: flagKey,
        user_id: userId,
        variant,
        context: context || (typeof window !== "undefined" ? window.location.pathname : null),
      }),
    });
  } catch {}
}

const loggedFlags = new Set<string>();

// ---------------------------------------------------------------------------
// Last-known-flags cache (snappy-boot, perf pass 2026-08-11).
//
// AppShell holds the ENTIRE site on a PageLoader while `loading` is true, so
// every visit used to pay a full round-trip to /api/config/feature-flags
// before anything painted. Returning visitors now render immediately from the
// last fetched flag set (localStorage, bounded age) while the fetch
// revalidates in the background — the same degraded-but-functional contract
// as the existing 10s-timeout fallback, except with *yesterday's real flags*
// instead of a blank map. First-ever visits (no cache) keep the old behavior.
// ---------------------------------------------------------------------------
const FLAGS_CACHE_KEY = "bac.featureFlags.v1";
const FLAGS_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Safety-critical flags are NEVER served from (or written to) the cache —
// they must only ever come from a live fetch or the shared fail-safe
// defaults. `feature_maintenance_mode` gates the whole site fail-closed
// (a stale cached "off" would let a browser render the live site for up
// to a day after maintenance engaged); `feature_payout_manual_approval`
// carries money-safe semantics. Stripping them from the seed means
// `resolveFlag` falls back to FEATURE_FLAG_DEFAULTS for exactly these
// keys until the live fetch lands — the same contract as the existing
// 10s-timeout fallback.
const CACHE_EXCLUDED_FLAGS = new Set<string>([
  "feature_maintenance_mode",
  "feature_payout_manual_approval",
  HOST_CONFIRMATION_CALLS_FLAG,
]);

function readCachedFlags(): Record<string, boolean> | null {
  try {
    const raw = window.localStorage.getItem(FLAGS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { flags?: unknown; savedAt?: unknown };
    if (
      !parsed ||
      typeof parsed !== "object" ||
      typeof parsed.savedAt !== "number" ||
      Date.now() - parsed.savedAt > FLAGS_CACHE_MAX_AGE_MS ||
      !parsed.flags ||
      typeof parsed.flags !== "object"
    ) {
      return null;
    }
    const out: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(parsed.flags as Record<string, unknown>)) {
      if (typeof v === "boolean" && !CACHE_EXCLUDED_FLAGS.has(k)) out[k] = v;
    }
    return out;
  } catch {
    return null; // Private mode / corrupt entry — behave as a miss.
  }
}

function writeCachedFlags(flags: Record<string, boolean>): void {
  try {
    const safe: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(flags)) {
      if (!CACHE_EXCLUDED_FLAGS.has(k)) safe[k] = v;
    }
    window.localStorage.setItem(
      FLAGS_CACHE_KEY,
      JSON.stringify({ flags: safe, savedAt: Date.now() }),
    );
  } catch {
    // Best-effort (quota / private mode) — just a slower next visit.
  }
}

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const { session, profile } = useUser();
  // Seed from the last-known cache so returning visitors paint immediately;
  // the fetch below always runs and overwrites (then re-persists) the truth.
  // Country-root SSR bootstrap: the first render is the anonymous default
  // flag set (never a stale per-browser cache, never a loading overlay); the
  // live fetch below still runs after hydration and applies the real flags.
  const ssrBootstrap = useSsrBootstrap();
  const request = useContext(SsrRequestContext);
  const [cachedSeed] = useState<Record<string, boolean> | null>(() =>
    ssrBootstrap ? request?.flags || {} : typeof window === "undefined" ? null : readCachedFlags(),
  );
  const [flags, setFlags] = useState<Record<string, boolean>>(
    () => cachedSeed ?? {},
  );
  const [loading, setLoading] = useState(cachedSeed === null);
  const channelRef = useRef<ReturnType<NonNullable<ReturnType<typeof getSupabaseClient>>["channel"]> | null>(null);

  const token = session?.access_token;
  const userId = profile?.id ? String(profile.id) : null;

  useEffect(() => {
    let cancelled = false;
    fetchFlags(token || undefined)
      .then(f => {
        if (!cancelled) {
          setFlags(f);
          setLoading(false);
          // Only persist a non-empty map: `{}` is the error/timeout
          // fallback shape and must not clobber a good cache.
          if (Object.keys(f).length > 0) writeCachedFlags(f);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    const sb = getSupabaseClient();
    if (!sb) return;

    // The realtime channel only provides *live* refresh of feature flags —
    // the flags themselves already load via the fetch path in the effect
    // above. If a client's browser/proxy/extension cannot open the
    // WebSocket, `.subscribe()` (or, in some environments, constructing the
    // underlying WebSocket) can throw synchronously or report a
    // CHANNEL_ERROR / TIMED_OUT status. Any of these must degrade silently
    // rather than surfacing as an uncaught error to Sentry.
    try {
      const channel = sb
        .channel("feature-flags-realtime")
        .on("postgres_changes", {
          event: "*",
          schema: "public",
          table: "feature_flags",
        }, () => {
          fetchFlags(token || undefined)
            .then(f => {
              setFlags(f);
              loggedFlags.clear();
            })
            .catch(() => {});
        })
        .subscribe((status) => {
          // `SUBSCRIBED` is the healthy path. `CHANNEL_ERROR`, `TIMED_OUT`
          // and `CLOSED` all mean live refresh is unavailable; the
          // fetch-loaded flags remain intact, so swallow these instead of
          // letting them propagate.
          if (
            status === "CHANNEL_ERROR" ||
            status === "TIMED_OUT" ||
            status === "CLOSED"
          ) {
            // Intentionally no-op: realtime is optional.
          }
        });

      channelRef.current = channel;
    } catch {
      // Constructing the channel / WebSocket threw (e.g. "WebSocket not
      // available" / invalid URL). Leave `channelRef` null so teardown is
      // a no-op and rely on the fetch path for flags.
      channelRef.current = null;
    }

    return () => {
      if (channelRef.current) {
        try {
          sb.removeChannel(channelRef.current);
        } catch {
          // removeChannel can throw if the channel never fully opened;
          // ignore so unmount stays clean.
        }
        channelRef.current = null;
      }
    };
  }, [token]);

  const isEnabled = useCallback((flagName: string): boolean => {
    if (loading) return false;
    // Known shared flags resolve through the shared default map so that
    // an unset row in `feature_flags` falls back to the same value the
    // mobile app sees, instead of silently returning `false` here and
    // `true` there (or vice versa).
    const value = isFeatureFlagKey(flagName)
      ? resolveFlag(flags, flagName as FeatureFlagKey)
      : flags[flagName];
    if (value === undefined) return false;

    const logKey = `${flagName}:${userId}`;
    if (typeof window !== "undefined" && !loggedFlags.has(logKey)) {
      loggedFlags.add(logKey);
      logFlagEvaluation(flagName, userId, value);
    }

    return value;
  }, [flags, loading, userId]);

  return (
    <FeatureFlagContext.Provider value={{ flags, loading, isEnabled }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

/**
 * Read a single feature flag.
 *
 * The signature accepts either a `FeatureFlagKey` (compile-time-checked
 * via `@workspace/feature-flags-shared`) or — for the rare ad-hoc /
 * experimental flag that hasn't been promoted to the shared union yet —
 * a free string. Once a flag has been added to
 * `@workspace/feature-flags-shared`, switch the call site to the
 * constant import to get the typo-proof guarantee.
 */
export function useFeatureFlag(flagName: FeatureFlagKey | (string & {})): boolean {
  const { isEnabled, loading } = useContext(FeatureFlagContext);
  if (loading) return false;
  return isEnabled(flagName);
}

/** Defaults map re-exported for tests / SSR fallbacks. */
export { FEATURE_FLAG_DEFAULTS };

export function useFeatureFlagLoading(): boolean {
  const { loading } = useContext(FeatureFlagContext);
  return loading;
}

export function useFeatureFlags() {
  return useContext(FeatureFlagContext);
}
